import { Recipe } from '../../api/Recipe';
import { apiModules } from '../../api/apiService';
import {
  hasReviewedLegacyFavorites,
  markLegacyFavoritesReviewed,
  readLegacyFavoritesCache,
  removeLegacyFavoritesCache,
} from './cache';

interface RefreshFavoritesForAuthorOptions {
  authorId?: number;
  setFavorites: (favorites: Recipe[]) => void;
  clearFavorites: () => void;
}

export const refreshFavoritesForAuthor = async ({
  authorId,
  setFavorites,
  clearFavorites,
}: RefreshFavoritesForAuthorOptions): Promise<void> => {
  if (!authorId) {
    clearFavorites();
    return;
  }

  const remoteFavorites = await apiModules.recipes.listMyFavorites();
  setFavorites(remoteFavorites);
};

interface InitializeFavoritesOptions {
  authorId: number;
  isCancelled: () => boolean;
  setFavorites: (favorites: Recipe[]) => void;
}

const listMissingLegacyRecipeIds = (
  remoteFavorites: Recipe[],
  legacyFavorites: Recipe[]
): string[] =>
  legacyFavorites
    .filter((recipe) => !remoteFavorites.some((favorite) => favorite.id === recipe.id))
    .map((recipe) => recipe.id);

const shouldSkipLegacyImport = (
  authorId: number,
  missingLegacyRecipeIds: string[]
): boolean => missingLegacyRecipeIds.length === 0 || hasReviewedLegacyFavorites(authorId);

const confirmLegacyFavoritesImport = (): boolean =>
  window.confirm(
    'Des favoris enregistres sur cet appareil ont ete detectes. Voulez-vous les importer dans votre compte ?'
  );

export const initializeFavoritesForAuthor = async ({
  authorId,
  isCancelled,
  setFavorites,
}: InitializeFavoritesOptions): Promise<void> => {
  const remoteFavorites = await apiModules.recipes.listMyFavorites();
  if (isCancelled()) {
    return;
  }

  setFavorites(remoteFavorites);

  const legacyFavorites = readLegacyFavoritesCache();
  const missingLegacyRecipeIds = listMissingLegacyRecipeIds(remoteFavorites, legacyFavorites);

  if (shouldSkipLegacyImport(authorId, missingLegacyRecipeIds)) {
    return;
  }

  const shouldImportLegacyFavorites = confirmLegacyFavoritesImport();

  if (!shouldImportLegacyFavorites) {
    markLegacyFavoritesReviewed(authorId);
    return;
  }

  await apiModules.recipes.addFavorites(missingLegacyRecipeIds, authorId);
  if (isCancelled()) {
    return;
  }

  removeLegacyFavoritesCache();
  markLegacyFavoritesReviewed(authorId);

  const mergedFavorites = await apiModules.recipes.listMyFavorites();
  if (isCancelled()) {
    return;
  }

  setFavorites(mergedFavorites);
};
